<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter, type RouteLocationRaw } from 'vue-router';
  // Icons
  import EyeIcon from '@/shared/assets/icons/EyeIcon.svg';
  // Components
  import { ColoredButton } from '@/shared/ui';
  // Composables
  import { useDocuments } from '@/features/Document';
  // Interfaces
  import { InfoDocumento } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      info: InfoDocumento[];
      documentId: string;
      hideButton?: boolean;
      /** Keeps the button on screen and inert, for a nature whose file view is not built yet. */
      disableViewAction?: boolean;
      /**
       * Destination of the view button. When absent the button delivers the revision file, which
       * is what it always did. When present the button navigates there instead, and it replaces
       * the history entry so the browser back button returns to the listing the user came from.
       */
      viewRoute?: RouteLocationRaw;
      viewButtonLabel?: string;
    }>(),
    {
      viewButtonLabel: 'Ver documento',
    }
  );

  const router = useRouter();
  const { viewDocument } = useDocuments();

  const revisionInfo = computed(() => {
    return props.info.find((item) => item.label === 'Revisão') ?? null;
  });
  const revisionNum = computed(() => {
    return parseInt(revisionInfo.value?.value?.split('.').pop() ?? '');
  });

  function openDocument(): void {
    if (props.disableViewAction) return;

    if (props.viewRoute) {
      router.replace(props.viewRoute);
      return;
    }

    viewDocument(props.documentId, revisionNum.value);
  }
</script>
<template>
  <div class="flex gap-6 items-center">
    <div class="flex gap-6 items-start flex-wrap">
      <div
        v-for="(item, index) in info"
        :key="index"
        class="flex flex-col gap-2"
      >
        <div class="font-semibold text-left">
          {{ item.label }}
        </div>
        <div
          class="rounded-2xl border flex items-center justify-center px-5 border-border-color bg-gray-02/20 h-12"
        >
          <div>{{ item.value }}</div>
        </div>
      </div>
    </div>

    <ColoredButton
      v-if="!hideButton"
      class="ml-auto px-4 py-3.5 shrink-0 whitespace-nowrap"
      :text="viewButtonLabel"
      :icon="EyeIcon"
      :icon-alt="viewButtonLabel"
      @click="openDocument"
    />
  </div>
</template>
